import Container, { Service } from 'typedi';
import { WindowToken } from '../../injection-tokens';
import { BrandUtils } from '../../../Modules/Utils/BrandUtils';
import {
    Brand,
    MarketingBrand,
    NavigateLinkId,
    StaticPaths,
} from '../../models/enums/general-enums';
import { LocalSimpleStoreService } from '../storage/implementations/simple-store';
import { LoaderManager } from '../../loaders/LoaderManager';
import { ICRM } from '../crm/models';

const languageMapping = new Map([['br', 'pt']]);

@Service()
export class UrlUtils {
    private _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _window: Window;

    private get _crmPromise(): Promise<ICRM> {
        return LoaderManager.Instance.CRMLoader.Instance;
    }

    constructor() {
        this._window = Container.get(WindowToken);
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
    }

    getDomain(url: string = ''): string {
        const formattedURL = new URL(url || '', this._window.location.origin);

        return formattedURL.hostname
            .split('.')
            .splice(formattedURL.hostname.split('.').length - 2)
            .join('.');
    }

    static getMappedLanguage = (lang: string): string => {
        return languageMapping.has(lang) ? languageMapping.get(lang)! : lang;
    };

    public getRelativeURL = (
        url: string,
        options: { removeLanguage?: boolean; removeBaseUrl?: boolean } = {
            removeLanguage: false,
            removeBaseUrl: false,
        },
    ): string => {
        if (!url) return url;

        const { removeLanguage, removeBaseUrl } = options || {};
        const urlToBeParsed = (removeLanguage && this.getNoLangURL(url)) || url;

        // Remove host and parse as relative to current origin
        const urlWithoutHost = this.getURLWithoutHost(urlToBeParsed);
        const parsedUrl = this.getParsedUrl(urlWithoutHost);

        // Remove hash
        parsedUrl.hash = '';

        // Rebuild the relative URL (pathname + search)
        let returnedUrl = parsedUrl.pathname + (parsedUrl.search || '');

        if (removeBaseUrl) {
            returnedUrl = this.getClientBaseURL(returnedUrl);
        }

        return returnedUrl;
    };

    /**
     * Deprecated
     * does not support empty url correctly
     * does not support querystring only while the location.pathname is not empty
     * please use getParsedUrlV2 instead
     * @param url
     */
    public getParsedUrl = (url: string): URL => {
        if (url.startsWith('#')) {
            // Use the current location's pathname and append the hash
            return new URL(this._window.location.pathname + url, this._window.location.origin);
        } else {
            return new URL(url, this._window.location.origin);
        }
    };

    /**
     * Parses a URL string and returns a URL object, resolving relative, hash, and absolute paths.
     *
     * Behavior:
     * - For hash-only URLs (e.g., '#/foo'), resolves as `{origin}{pathname}#/foo`.
     * - For absolute paths (e.g., '/bar'), resolves as `{origin}/bar`.
     * - For query strings (e.g., '?baz=1'), resolves as `{origin}{pathname}?baz=1`.
     * - For full URLs, return the parsed URL.
     *
     * The base used is `{origin}{pathname}` so hash and query strings are resolved relative to the current page.
     * Absolute paths replace the current pathname.
     */
    public getParsedUrlV2 = (url: string): URL => {
        return new URL(url, this._window.location.origin + this._window.location.pathname);
    };

    public getLangURL = (url: string): string => {
        const {
            langId: CurrentLang,
            defaultLang,
            contentLangId: ContentLang,
        } = this._window.pageContextManager.getLocalizationData();

        const { brandId, marketingBrandId } = this._window.pageContextManager.getBrandData();

        const mappedContentLang = UrlUtils.getMappedLanguage(ContentLang);

        if (BrandUtils.isPokerClient() === true || marketingBrandId === MarketingBrand.MrGreen) {
            return url;
        }

        if (
            !url ||
            typeof url !== 'string' ||
            brandId === Brand.Sport_CA_ON ||
            brandId === Brand.Sport_CA_AB
        )
            return url;

        if (CurrentLang === defaultLang && CurrentLang === mappedContentLang) return url;

        if (
            url.indexOf(`/${defaultLang}/`) >= 0 ||
            url.indexOf(`/${CurrentLang}/`) >= 0 ||
            url.indexOf(`/${mappedContentLang}/`) >= 0
        )
            return url;

        return `/${mappedContentLang}${url}`;
    };

    public getNoLangURL = (url: string) => {
        if (!url || typeof url !== 'string') return url;
        const contentLangId = this._window.pageContextManager.getLocalizationData().contentLangId;

        return url.replace(`/${contentLangId}/`, '/');
    };

    public getURLWithQueryString = (url: string, queryString: string) => {
        if (!url) return undefined;

        const parsedUrl = this.getParsedUrl(url);
        // Parse the new query string and append/override params
        const newParams = new URLSearchParams(queryString);
        newParams.forEach((value, key) => {
            parsedUrl.searchParams.set(key, value);
        });

        // Remove hash if present (to match previous logic)
        parsedUrl.hash = '';

        return parsedUrl.href;
    };

    public getCurrentUrl = () => {
        return this._window.location?.href;
    };

    public getURLWithSCut = (url: string): string => {
        if (!url) {
            return undefined;
        }

        let sCut = this._window.sCut;
        if (sCut !== null && typeof sCut === 'object') {
            sCut = sCut.queryString();
        }
        if (!sCut || typeof sCut !== 'string') {
            return url;
        }

        const parsedUrl = this.getParsedUrl(url);
        // Parse sCut as query string and merge with existing params
        const sCutParams = new URLSearchParams(sCut);
        sCutParams.forEach((value, key) => {
            parsedUrl.searchParams.set(key, value);
        });

        // Remove hash if present
        parsedUrl.hash = '';

        // Return the relative path with updated query string
        return parsedUrl.href;
    };

    public getURLWithoutHost = (url: string): string => {
        if (!url) return url;

        return url.replace(/.*\/\/[^\/]*/, '');
    };

    public removeParamsFromQuery = (url: string, parameter: string | number | boolean) => {
        //prefer to use l.search if you have a location/link object
        const urlParts = url.split('?');
        if (urlParts.length >= 2) {
            const prefix = encodeURIComponent(parameter) + '=';
            const pars = urlParts[1].split(/[&;]/g);

            //reverse iteration as may be destructive
            for (let i = pars.length; i-- > 0; ) {
                //idiom for string.startsWith
                if (pars[i].lastIndexOf(prefix, 0) !== -1) {
                    pars.splice(i, 1);
                }
            }

            return urlParts[0] + (pars.length > 0 ? '?' + pars.join('&') : '');
        } else {
            const deepLinkIndex = url.indexOf('#deeplink');
            if (deepLinkIndex !== -1) {
                url = url.substring(0, deepLinkIndex - 1);
            }
        }
        return url;
    };

    public getQueryStringParameterByName = (name): any => {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
        // search for the parameter value, case-insensitive
        const regex = new RegExp('[\\?&]' + name + '=([^&#]*)', 'i'),
            results = regex.exec(this._window.location.search);

        return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    };

    public redirectToUrl = (url: string): void => {
        if (url) {
            this._window.location.href = url;
        }
    };

    public getParameterByName = (name: string, tokenMode?: boolean) => {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
        // search for the parameter value, case-insensitive
        const regex = new RegExp('[\\?&]' + name + '=([^&#]*)', 'i'),
            results = regex.exec(this._window.location?.search);

        if (tokenMode == true) {
            return results === null ? '' : decodeURIComponent(results[1]);
        } else {
            return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
        }
    };

    public isAutologinRequest = () => {
        return !!this.getQueryStringParameterByName('UserInfo');
    };

    public isClientMode(): boolean {
        let isClientMode = this._window.IsClientMode || false;
        if (!isClientMode) {
            const currentSearchParams = new URLSearchParams(this._window.location?.search);
            isClientMode =
                currentSearchParams.get('LinkId') === NavigateLinkId.ToSportOffers.toString();
            if (isClientMode) {
                this._window.IsClientMode = true;
            }
        }

        return isClientMode;
    }

    public getLinkId(): NavigateLinkId {
        const linkId = this.getQueryStringParameterByName('LinkId');
        const enumProperty = NavigateLinkId[linkId];
        if (enumProperty) {
            return NavigateLinkId[enumProperty];
        }

        return undefined;
    }

    public hasReferrer(): boolean {
        const url = new URL(this._window.location?.href);
        return url.searchParams.has('referrer');
    }

    public clearQueryStringParams(): void {
        this._window.history.replaceState(null, '', this._window.location?.href.split('?')[0]);
    }

    public getDeeplinkHash(): string {
        const url = new URL(this._window.location?.href);
        const urlHash = url.hash?.substring(1); // Remove the leading '#'
        if (!!urlHash) {
            const deeplinkHash = decodeURIComponent(urlHash);
            if (deeplinkHash.split('=')[0] === 'deeplink') {
                return deeplinkHash.split('=')[1];
            }
        }
        return null;
    }

    public getNativeUXVersion = () => {
        const parameterName = 'nativeUXVersion';

        let version = this.getParameterByName(parameterName);

        if (!version) version = this._localSimpleStoreService.get(parameterName);

        const versionNumber = parseInt(version);

        return isNaN(versionNumber) ? 0 : versionNumber;
    };

    public historyPushState = (params, title, url) => {
        this._window.history.pushState(params, title, url);
    };

    public historyReplaceState = (title: string, url: string) => {
        this._window.history.replaceState({}, title, url);
    };

    public getClientBaseURL = (relativeURL: string) => {
        if (!relativeURL) return relativeURL;

        let clientPath = this.getNoLangURL(relativeURL);

        const clientBaseUrl = this._window.pageContextManager.getSiteData().clientBaseUrl;
        if (
            clientBaseUrl &&
            clientBaseUrl !== StaticPaths.Root &&
            clientPath.startsWith(clientBaseUrl)
        ) {
            clientPath = clientPath.replace(`${clientBaseUrl}/`, StaticPaths.Root);
        }

        return clientPath;
    };

    public getURLWithBase = (url: string) => {
        if (url === null || url === undefined) return url;
        const clientBaseUrl = this._window.pageContextManager.getSiteData().clientBaseUrl;
        if (clientBaseUrl !== StaticPaths.Root) {
            const relativeURL = this.getRelativeURL(url, {
                removeBaseUrl: true,
                removeLanguage: true,
            });

            return `${clientBaseUrl}${relativeURL}`;
        }

        return url;
    };

    public recordToQueryStr(params: Record<string, string>): string {
        return Object.entries(params)
            .filter(([key, value]) => key && value)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
    }

    public async openMobileAppWithFallback(
        appUrl: string,
        fallbackUrlAndroid: string,
        fallbackUrlIOS: string,
    ): Promise<void> {
        const deviceData = this._window.pageContextManager.getDeviceData();

        if (!deviceData.isMobile) {
            return;
        }

        if (deviceData.isIOS) {
            if (deviceData.isHybrid) {
                const externalLinkModel = { Url: appUrl };

                setTimeout(async () => {
                    (await this._crmPromise).onExternalLinkOpen({ Url: fallbackUrlIOS });
                }, 250);

                (await this._crmPromise).onExternalLinkOpen(externalLinkModel);
            } else {
                setTimeout(() => {
                    this._window.open(fallbackUrlIOS, '_blank');
                    this._window.location.assign(fallbackUrlIOS);
                }, 250);

                this._window.open(appUrl, '_blank');
                this._window.location.assign(appUrl);
            }
        } else {
            // Android
            this.redirectToUrl(appUrl);
            setTimeout(() => {
                this._window.open(fallbackUrlAndroid, '_blank');
            }, 1000);
        }
    }
}
